'use client';

import { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Testimonial {
  id: number;
  image: string;
  alt: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/המלצותשרון1_84e098c3.jpeg',
    alt: 'המלצה מלקוח - וידאו אימון עם שרון',
  },
  {
    id: 2,
    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/המלצותשרון2_6699596f.jpeg',
    alt: 'המלצה מלקוח - אימון בבית',
  },
  {
    id: 3,
    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/המלצותשרון3_206ad6f4.jpeg',
    alt: 'המלצה מלקוח - תמיכה ועידוד',
  },
  {
    id: 4,
    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/המלצותשרון4_9da692da.jpeg',
    alt: 'המלצה מלקוח - הודעת תודה',
  },
  {
    id: 5,
    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/המלצותשרון5_1e5eae17.jpeg',
    alt: 'המלצה מלקוח - משוב חיובי',
  },
  {
    id: 6,
    image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/המלצותשרון6_b143e493.jpeg',
    alt: 'המלצה מלקוח - תודה מהלב',
  },
];

export default function ClientLove() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selectedTestimonial = testimonials.find((t) => t.id === selectedId);

  return (
    <section
      id="client-love"
      className="py-24 px-4 bg-gradient-to-b from-[#F5F0E8] to-white"
      style={{ direction: 'rtl' }}
    >
      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{
              fontFamily: 'var(--font-header)',
              color: '#2D5A3D',
            }}
          >
            מהרגעים האלה שמרגשים אותי...
          </h2>
          <p
            className="text-lg md:text-xl"
            style={{
              fontFamily: 'var(--font-body)',
              color: '#6B7280',
            }}
          >
            פרגונים מהצאט שנותנים לי כוח להמשיך
          </p>
        </motion.div>

        {/* Masonry Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={false}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => setSelectedId(testimonial.id)}
              className="cursor-pointer group"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <img
                  src={testimonial.image}
                  alt={testimonial.alt}
                  width={400}
                  height={300}
                  className="w-full h-auto object-cover"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div
                      className="bg-white/90 px-4 py-2 rounded-full text-sm font-semibold"
                      style={{
                        color: '#2D5A3D',
                        fontFamily: 'var(--font-body)',
                      }}
                    >
                      הגדל
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile Carousel Info */}
        <div className="md:hidden text-center">
          <p
            className="text-sm"
            style={{
              fontFamily: 'var(--font-body)',
              color: '#9CA3AF',
            }}
          >
            לחץ על כל תמונה כדי להגדיל
          </p>
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedTestimonial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedId(null)}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-2xl w-full"
            >
              <img
                src={selectedTestimonial.image}
                alt={selectedTestimonial.alt}
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
              <button
                onClick={() => setSelectedId(null)}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white p-2 rounded-full transition-all duration-200"
              >
                <X size={24} className="text-gray-800" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
